import { arcControlPoint, arrowLength, MIN_ARROW_LENGTH } from './arrowPath';
import {
  clamp,
  distanceToRect,
  rectCenter,
  rectsOverlap,
  type Point,
  type Rect,
  type Size,
} from './geometry';

/**
 * Single source of truth for "where does the card go and where does the arrow run".
 * Card position and arrow endpoints are computed together so they can never disagree,
 * and every number is derived from the live viewport instead of fixed desktop offsets.
 */

/** Which side of the spotlight the card sits on. */
export type PlacementSide = 'top' | 'bottom' | 'left' | 'right';
export type PlacementPreference = PlacementSide | 'auto';

export interface ArrowAnchors {
  from: Point;
  to: Point;
  /** Sideways offset of the arc's control point. Sign picks which way it bows. */
  bow: number;
}

export interface Placement {
  card: Rect;
  /**
   * Height budget for the card on the chosen side. The card scrolls inside this
   * instead of growing over the spotlight — the only way to keep a tall step
   * readable on a short phone.
   */
  cardMaxHeight: number;
  side: PlacementSide | 'center';
  arrow: ArrowAnchors | null;
}

export interface PlacementOptions {
  /** Region the card must stay clear of — the spotlight (union of all holes). */
  spotlight: Rect | null;
  /** Region the arrow points at. Defaults to `spotlight`. */
  anchor?: Rect | null;
  card: Size;
  viewport: Size;
  /** Step preference. Honoured when it fits, otherwise the best fitting side wins. */
  preference?: PlacementPreference;
  showArrow?: boolean;
}

export const VIEWPORT_MARGIN = 16;

/** Room reserved for the curve. Vertical arcs get more space than horizontal ones. */
const ARROW_GAP = { vertical: 140, horizontal: 96 };
const ARROW_GAP_COMPACT = { vertical: 88, horizontal: 64 };
const MIN_ARROW_GAP = { vertical: 56, horizontal: 44 };
/**
 * The minimum has to leave a *drawable* arrow behind: the two endpoints give up
 * `TIP_STANDOFF` + 4px of the gap, and anything shorter than ~26px is rejected as
 * unreadable. A tighter minimum silently produced steps with no arrow at all.
 */
const MIN_ARROW_GAP_COMPACT = { vertical: 56, horizontal: 46 };
/** Gap used when the step draws no arrow. */
const PLAIN_GAP = { vertical: 20, horizontal: 20 };

/** How far the arrowhead stops short of the spotlight border. */
const TIP_STANDOFF = 10;
/** Keeps the arrow's card-side anchor away from the rounded corners. */
const EDGE_INSET = 32;

const COMPACT_WIDTH = 700;
const COMPACT_HEIGHT = 640;
/** Below this width there is no usable room beside the spotlight. */
const NARROW_WIDTH = 560;

/**
 * Bow grows sub-linearly: short hops still read as a curve, long ones stay a
 * gentle lean instead of ballooning into a giant sweep across the page.
 */
const BOW_BASE = 28;
const BOW_RATIO = 0.12;
const MIN_BOW = 32;
const MAX_BOW = 68;

/** A card squeezed below this is unreadable; we accept an overlap rather than shrink further. */
const MIN_CARD_HEIGHT = 280;
/**
 * ...except on a short screen, where a fixed floor would be most of the viewport and
 * leave the arrow nowhere to run. A landscape phone gets a shorter, scrolling card.
 */
const MIN_CARD_HEIGHT_RATIO = 0.45;

/** How far inside the spotlight an arrow target must sit before it steers the card. */
const MIN_ANCHOR_INSET = 24;

const ALL_SIDES: PlacementSide[] = ['bottom', 'top', 'right', 'left'];
const VERTICAL_SIDES: PlacementSide[] = ['bottom', 'top'];

export function isCompactViewport(viewport: Size): boolean {
  return viewport.width < COMPACT_WIDTH || viewport.height < COMPACT_HEIGHT;
}

/** Map the step's authoring vocabulary onto a side. */
export function sideForPlacement(
  placement: 'above' | 'below' | 'left' | 'right' | 'auto' | undefined
): PlacementPreference {
  switch (placement) {
    case 'above':
      return 'top';
    case 'below':
      return 'bottom';
    case 'left':
      return 'left';
    case 'right':
      return 'right';
    default:
      return 'auto';
  }
}

function centeredCard(card: Size, viewport: Size): Rect {
  return {
    x: clamp((viewport.width - card.width) / 2, VIEWPORT_MARGIN, maxX(card, viewport)),
    y: clamp((viewport.height - card.height) / 2, VIEWPORT_MARGIN, maxY(card, viewport)),
    width: card.width,
    height: card.height,
  };
}

function maxX(card: Size, viewport: Size): number {
  return Math.max(VIEWPORT_MARGIN, viewport.width - card.width - VIEWPORT_MARGIN);
}

function maxY(card: Size, viewport: Size): number {
  return Math.max(VIEWPORT_MARGIN, viewport.height - card.height - VIEWPORT_MARGIN);
}

function freeSpace(spotlight: Rect, viewport: Size): Record<PlacementSide, number> {
  return {
    top: spotlight.y - VIEWPORT_MARGIN,
    bottom: viewport.height - (spotlight.y + spotlight.height) - VIEWPORT_MARGIN,
    left: spotlight.x - VIEWPORT_MARGIN,
    right: viewport.width - (spotlight.x + spotlight.width) - VIEWPORT_MARGIN,
  };
}

function gapsFor(showArrow: boolean, compact: boolean) {
  if (!showArrow) return { ideal: PLAIN_GAP, min: PLAIN_GAP };
  return compact
    ? { ideal: ARROW_GAP_COMPACT, min: MIN_ARROW_GAP_COMPACT }
    : { ideal: ARROW_GAP, min: MIN_ARROW_GAP };
}

/**
 * Room a side can actually give the card, independent of how tall the card wants
 * to be — that independence is what stops the card size and the chosen side from
 * chasing each other frame after frame.
 */
function availableSpace(
  free: Record<PlacementSide, number>,
  viewport: Size,
  gaps: ReturnType<typeof gapsFor>
): { height: Record<PlacementSide, number>; width: Record<PlacementSide, number> } {
  const fullHeight = viewport.height - 2 * VIEWPORT_MARGIN;
  const fullWidth = viewport.width - 2 * VIEWPORT_MARGIN;
  return {
    height: {
      top: free.top - gaps.min.vertical,
      bottom: free.bottom - gaps.min.vertical,
      left: fullHeight,
      right: fullHeight,
    },
    width: {
      top: fullWidth,
      bottom: fullWidth,
      left: free.left - gaps.min.horizontal,
      right: free.right - gaps.min.horizontal,
    },
  };
}

function placeOnSide(
  side: PlacementSide,
  spotlight: Rect,
  card: Size,
  viewport: Size,
  free: Record<PlacementSide, number>,
  gaps: ReturnType<typeof gapsFor>
): Rect {
  const center = rectCenter(spotlight);
  const vertical = side === 'top' || side === 'bottom';
  const axis = vertical ? 'vertical' : 'horizontal';
  const extent = vertical ? card.height : card.width;
  // Use the full gap when it fits, shrink towards the minimum when it does not.
  const gap = clamp(free[side] - extent, gaps.min[axis], gaps.ideal[axis]);

  if (vertical) {
    const y =
      side === 'bottom'
        ? spotlight.y + spotlight.height + gap
        : spotlight.y - card.height - gap;
    return {
      x: clamp(center.x - card.width / 2, VIEWPORT_MARGIN, maxX(card, viewport)),
      y: clamp(y, VIEWPORT_MARGIN, maxY(card, viewport)),
      width: card.width,
      height: card.height,
    };
  }

  const x =
    side === 'right'
      ? spotlight.x + spotlight.width + gap
      : spotlight.x - card.width - gap;
  return {
    x: clamp(x, VIEWPORT_MARGIN, maxX(card, viewport)),
    y: clamp(center.y - card.height / 2, VIEWPORT_MARGIN, maxY(card, viewport)),
    width: card.width,
    height: card.height,
  };
}

/**
 * When a step spotlights a whole region but aims its arrow at one element inside
 * it, the card belongs on the spotlight edge that element hugs — otherwise the
 * arrow has to cross the entire highlighted region to reach it. Which edge that
 * is depends on the layout, not on the step: "Show All" sits at the table's top
 * right on desktop and at its bottom once the table stacks on a phone.
 */
function anchorHuggedSide(spotlight: Rect, anchor: Rect): PlacementSide | null {
  if (!rectsOverlap(spotlight, anchor)) return null;
  const distance: Record<PlacementSide, number> = {
    top: anchor.y - spotlight.y,
    bottom: spotlight.y + spotlight.height - (anchor.y + anchor.height),
    left: anchor.x - spotlight.x,
    right: spotlight.x + spotlight.width - (anchor.x + anchor.width),
  };
  // An anchor that fills the spotlight sits at every edge at once and has no
  // opinion; only a target clearly tucked against one edge does.
  const inset = Math.max(...ALL_SIDES.map(side => distance[side]));
  if (inset < MIN_ANCHOR_INSET) return null;
  return ALL_SIDES.reduce((best, side) => (distance[side] < distance[best] ? side : best), 'top' as PlacementSide);
}

/**
 * Which side of the *arrow's* anchor the card ended up on. Derived from the two
 * boxes rather than reused from the card placement, because a step can spotlight
 * a whole table while aiming its arrow at one small link inside it.
 */
function arrowSide(card: Rect, anchor: Rect): PlacementSide {
  const clearance: Record<PlacementSide, number> = {
    right: card.x - (anchor.x + anchor.width),
    left: anchor.x - (card.x + card.width),
    bottom: card.y - (anchor.y + anchor.height),
    top: anchor.y - (card.y + card.height),
  };
  return ALL_SIDES.reduce((best, side) => (clearance[side] > clearance[best] ? side : best), 'bottom' as PlacementSide);
}

/**
 * Prefer the arc that stays on screen, keeps clear of both boxes and leans into
 * the roomier half of the viewport. Deterministic, so the arrow never flickers.
 */
function scoreControlPoint(control: Point, card: Rect, anchor: Rect, viewport: Size): number {
  const margin = 12;
  const offscreen =
    control.x < margin ||
    control.y < margin ||
    control.x > viewport.width - margin ||
    control.y > viewport.height - margin;

  return (
    (offscreen ? -1000 : 0) +
    Math.min(distanceToRect(control, card), 160) +
    Math.min(distanceToRect(control, anchor), 160) * 0.5 +
    Math.min(control.x, control.y, viewport.width - control.x, viewport.height - control.y) * 0.3
  );
}

function pickBow(from: Point, to: Point, card: Rect, anchor: Rect, viewport: Size): number {
  const magnitude = clamp(BOW_BASE + arrowLength(from, to) * BOW_RATIO, MIN_BOW, MAX_BOW);
  const positive = scoreControlPoint(arcControlPoint(from, to, magnitude), card, anchor, viewport);
  const negative = scoreControlPoint(arcControlPoint(from, to, -magnitude), card, anchor, viewport);
  return positive >= negative ? magnitude : -magnitude;
}

/**
 * Tail on the card edge that faces the anchor, head just outside the anchor edge
 * that faces the card, both pulled towards the anchor's centre line. Direction
 * falls out of the geometry instead of a per-case branch.
 */
function arrowAnchors(card: Rect, anchor: Rect, viewport: Size): ArrowAnchors | null {
  if (rectsOverlap(card, anchor, 4)) return null;
  const side = arrowSide(card, anchor);

  const cardInsetX = clamp(EDGE_INSET, 8, card.width / 2);
  const cardInsetY = clamp(EDGE_INSET, 8, card.height / 2);
  const anchorInsetX = clamp(EDGE_INSET, 4, anchor.width / 2);
  const anchorInsetY = clamp(EDGE_INSET, 4, anchor.height / 2);
  const center = rectCenter(anchor);

  let from: Point;
  let to: Point;

  if (side === 'bottom' || side === 'top') {
    const x = clamp(center.x, card.x + cardInsetX, card.x + card.width - cardInsetX);
    from = { x, y: side === 'bottom' ? card.y - 4 : card.y + card.height + 4 };
    to = {
      x: clamp(x, anchor.x + anchorInsetX, anchor.x + anchor.width - anchorInsetX),
      y: side === 'bottom' ? anchor.y + anchor.height + TIP_STANDOFF : anchor.y - TIP_STANDOFF,
    };
  } else {
    const y = clamp(center.y, card.y + cardInsetY, card.y + card.height - cardInsetY);
    from = { x: side === 'right' ? card.x - 4 : card.x + card.width + 4, y };
    to = {
      x: side === 'right' ? anchor.x + anchor.width + TIP_STANDOFF : anchor.x - TIP_STANDOFF,
      y: clamp(y, anchor.y + anchorInsetY, anchor.y + anchor.height - anchorInsetY),
    };
  }

  // The renderer has the final say on what is too short to draw. Reporting an arrow
  // it would then discard is worse than reporting none: side selection below uses
  // this answer to decide where the card goes.
  if (arrowLength(from, to) < MIN_ARROW_LENGTH) return null;

  return { from, to, bow: pickBow(from, to, card, anchor, viewport) };
}

export function computePlacement(options: PlacementOptions): Placement {
  const { spotlight, card, viewport, preference = 'auto', showArrow = false } = options;
  const anchor = options.anchor ?? spotlight;

  const fullHeight = viewport.height - 2 * VIEWPORT_MARGIN;

  if (!spotlight || card.width <= 0 || card.height <= 0) {
    return {
      card: centeredCard(card, viewport),
      cardMaxHeight: fullHeight,
      side: 'center',
      arrow: null,
    };
  }

  const gaps = gapsFor(showArrow, isCompactViewport(viewport));
  const usable = viewport.width < NARROW_WIDTH ? VERTICAL_SIDES : ALL_SIDES;
  const minCardHeight = Math.min(MIN_CARD_HEIGHT, viewport.height * MIN_CARD_HEIGHT_RATIO);
  const wantsArrow = showArrow && Boolean(anchor);

  const hugged = wantsArrow && anchor ? anchorHuggedSide(spotlight, anchor) : null;

  /** Can this region be cleared at all, at the smallest card we are willing to draw? */
  const clearable = (region: Rect) => {
    const space = availableSpace(freeSpace(region, viewport), viewport, gaps);
    return usable.some(side => space.width[side] >= card.width && space.height[side] >= minCardHeight);
  };

  // Normally the card clears the whole spotlight. But a phone-sized screen can be
  // smaller than the spotlight itself — a revenue table, say — and then no placement
  // clears it and the card is dropped wherever it fits, leaving the arrow to cross
  // the entire highlighted region to reach its target. Keeping clear of just the
  // target instead puts the card right next to the thing it is talking about; the
  // overlap with the rest of the table was unavoidable either way.
  const obstacle = hugged && anchor && !clearable(spotlight) ? anchor : spotlight;
  const free = freeSpace(obstacle, viewport);
  const available = availableSpace(free, viewport, gaps);

  // The budget is the best any candidate side could offer, decided *before* the side
  // is chosen and from the card's width only. Deriving it from the chosen *height*
  // instead would let the two chase each other: a shorter card makes another side
  // look better, which hands back a different budget, which changes the height again.
  // A side too narrow for the card is left out — counting the full viewport height of
  // a side that cannot host the card at all inflates the budget and the card then
  // lands on top of the spotlight.
  const roomy = usable.filter(side => available.width[side] >= card.width);
  const cardMaxHeight = clamp(
    Math.max(...(roomy.length > 0 ? roomy : usable).map(side => available.height[side])),
    minCardHeight,
    fullHeight
  );
  const constrained: Size = { width: card.width, height: Math.min(card.height, cardMaxHeight) };

  /** How much of the card a side can host, 1 or more meaning "fits as-is". */
  const fitRatio = (side: PlacementSide) =>
    Math.min(available.height[side] / constrained.height, available.width[side] / constrained.width);

  // A separate arrow target outranks the step's own preference: that preference was
  // authored against the desktop layout, while the target's position is live.
  const wanted = (obstacle === spotlight ? hugged : null) ?? preference;

  // Every side is placed and its arrow resolved *before* one is picked. Choosing the
  // side first and drawing the arrow afterwards is what left steps arrow-less: the
  // roomiest side can be the one where the card lands on top of the arrow's target.
  const candidates = usable.map(side => {
    const rect = placeOnSide(side, obstacle, constrained, viewport, free, gaps);
    return {
      side,
      rect,
      arrow: wantsArrow && anchor ? arrowAnchors(rect, anchor, viewport) : null,
      fit: fitRatio(side),
    };
  });

  const best = candidates.slice().sort((a, b) => {
    // Holding the whole card comes first — a scrolling card is worse than a moved one.
    if ((a.fit >= 1) !== (b.fit >= 1)) return a.fit >= 1 ? -1 : 1;
    if (wantsArrow && Boolean(a.arrow) !== Boolean(b.arrow)) return a.arrow ? -1 : 1;
    if ((a.side === wanted) !== (b.side === wanted)) return a.side === wanted ? -1 : 1;
    return b.fit - a.fit;
  })[0];

  return {
    card: best.rect,
    cardMaxHeight,
    side: best.side,
    arrow: best.arrow,
  };
}
